import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import RosterBackendService from '../../services/RosterBackendService';
import { getStyles } from './styles';
import { Loading } from '../Common/Layout/Loading';

export default function StandardSetSync({
  onSuccess,
}: {
  onSuccess: () => void;
}) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [selectedPublisher, setSelectedPublisher] = useState<string>();
  const [selectedSubject, setSelectedSubject] = useState<string>();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    if (!selectedPublisher) {
      setErrorMessage('You must select a publisher before submitting.');
      setIsLoading(false);
      return;
    }

    const response = await RosterBackendService.queueAssetSyncByStandardSet({
      publisherCode: selectedPublisher,
      subjectCode: selectedSubject,
    }, { timeout: 30000 });

    if (response.isOk()) {
      setIsLoading(false);
      onSuccess();
      return;
    }

    setIsLoading(false);

    const errorMessage = response.error.result.isJust() && response.error.result.value.message?.includes('Not Found') ? 
      'There are no assets associated to standards for the selected publisher and subject.' :
      'We\'re sorry, there was an error saving this standard set.';
    setErrorMessage(errorMessage);
  };

  return (
    <form data-testid='form' onSubmit={handleSubmit}>
      <FormControl component="fieldset" disabled={isLoading} sx={styles.fieldSetStyle}>
        <PublishersDropdown
          value={selectedPublisher}
          onChange={(e) => {
            setErrorMessage('');
            setSelectedPublisher(e.target.value);
          }}
        />
        {selectedPublisher && (
          <SubjectsDropdown
            value={selectedSubject}
            onChange={(e) => {
              setErrorMessage('');
              setSelectedSubject(e.target.value);
            }}
          />
        )}
        {errorMessage && (
          <Typography sx={{ color: 'red' }}>{errorMessage}</Typography>
        )}
        <Box sx={styles.button}>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            disabled={!selectedPublisher || isLoading}
          >
            {isLoading && <Loading size={10} height='10px' width='10px'/>}
            Submit
          </Button>
        </Box>
      </FormControl>
    </form>
  );
}

type DropdownProps = {
  value: string | undefined;
  onChange: (event: SelectChangeEvent<any>, child: React.ReactNode) => void;
};

function PublishersDropdown({ value, onChange }: DropdownProps) {
  const [publishers, setPublishers] = useState<string[]>([]);

  useEffect(() => {
    const getPublishers = async () => {
      const result = await RosterBackendService.getStandardsPublishers({});

      if (result.isOk()) {
        setPublishers(result.value.publisherCodes);
      }
    };

    getPublishers();
  }, []);

  if (!publishers.length) {
    return <Typography>Loading... </Typography>;
  }

  return (
    <FormControl fullWidth>
      <InputLabel id='publisher-select-label'>Publisher</InputLabel>
      <Select
        labelId='publisher-select-label'
        value={value || ''}
        label='Publisher'
        onChange={onChange}
      >
        {publishers.map((publisher) => (
          <MenuItem key={publisher} value={publisher}>
            {publisher}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function SubjectsDropdown({ value, onChange }: DropdownProps) {
  const [subjects, setSubjects] = useState<string[]>([]);

  useEffect(() => {
    const getSubjects = async () => {
      const result = await RosterBackendService.getStandardsSubjects({});

      if (result.isOk()) {
        const subjects = result.value.subjects.map((s) => s.code);
        setSubjects(subjects);
      }
    };

    getSubjects();
  }, []);

  if (!subjects.length) {
    return <Typography>Loading... </Typography>;
  }

  return (
    <FormControl fullWidth sx={{ marginTop: '16px' }}>
      <InputLabel id='subject-select-label'>Subject</InputLabel>
      <Select
        labelId='subject-select-label'
        value={value || ''}
        label='Subject'
        onChange={onChange}
      >
        {subjects.map((subject) => (
          <MenuItem key={subject} value={subject}>
            {subject}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
